/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import numelDimension from '@stdlib/ndarray/base/numel-dimension';
import getStride from '@stdlib/ndarray/base/stride';
import getOffset from '@stdlib/ndarray/base/offset';
import getData from '@stdlib/ndarray/base/data-buffer';
import ndarraylike2scalar from '@stdlib/ndarray/base/ndarraylike2scalar';
import { ndarray as strided } from './../../../../strided/varianceyc';


// MAIN //

/**
* Computes the variance of a one-dimensional ndarray using a one-pass algorithm proposed by Youngs and Cramer.
*
* ## Notes
*
* -   The function expects the following ndarrays:
*
*     -   a one-dimensional input ndarray.
*     -   a zero-dimensional ndarray specifying the degrees of freedom adjustment.
*
* @param {ArrayLikeObject<Object>} arrays - array-like object containing ndarrays
* @returns {number} variance
*
* @example
* import vector from '@stdlib/ndarray/vector/ctor';
* import scalar2ndarray from '@stdlib/ndarray/from-scalar';
*
* var x = vector( [ 2.0, 1.0, 2.0, -2.0, -2.0, 2.0, 3.0, 4.0 ], 'generic' );
*
* var correction = scalar2ndarray( 1.0, {
*     'dtype': 'generic'
* });
*
* var v = varianceyc( [ x, correction ] );
* // returns ~4.79
*/
function varianceyc( arrays ) {
	var correction;
	var x;

	x = arrays[ 0 ];
	correction = ndarraylike2scalar( arrays[ 1 ] );

	return strided( numelDimension( x, 0 ), correction, getData( x ), getStride( x, 0 ), getOffset( x ) ); // eslint-disable-line max-len
}


// EXPORTS //

export default varianceyc;
